"use strict";

/**
 * Map Google Places / short region codes to catalog region labels
 * aligned with stateAndCityJson (and forms.properties state.cl.*).
 */
const REGION_ALIAS_TO_LABEL = {
    rm: "Región Metropolitana de Santiago",
    "región metropolitana": "Región Metropolitana de Santiago",
    "region metropolitana": "Región Metropolitana de Santiago",
    "región metropolitana de santiago": "Región Metropolitana de Santiago",
    "region metropolitana de santiago": "Región Metropolitana de Santiago",
    ta: "Región de Tarapacá",
    tarapacá: "Región de Tarapacá",
    tarapaca: "Región de Tarapacá",
    an: "Región de Antofagasta",
    antofagasta: "Región de Antofagasta",
    at: "Región de Atacama",
    atacama: "Región de Atacama",
    co: "Región de Coquimbo",
    coquimbo: "Región de Coquimbo",
    va: "Región de Valparaíso",
    valparaíso: "Región de Valparaíso",
    valparaiso: "Región de Valparaíso",
    oh: "Región del Libertador General Bernardo O'Higgins",
    "o'higgins": "Región del Libertador General Bernardo O'Higgins",
    maule: "Región del Maule",
    ma: "Región del Maule",
    nb: "Región de Ñuble",
    ñuble: "Región de Ñuble",
    nuble: "Región de Ñuble",
    bi: "Región del Biobío",
    biobío: "Región del Biobío",
    biobio: "Región del Biobío",
    ar: "Región de la Araucanía",
    araucanía: "Región de la Araucanía",
    araucania: "Región de la Araucanía",
    lr: "Región de Los Ríos",
    "los ríos": "Región de Los Ríos",
    "los rios": "Región de Los Ríos",
    ll: "Región de Los Lagos",
    "los lagos": "Región de Los Lagos",
    ai: "Región de Aysén",
    aysén: "Región de Aysén",
    aysen: "Región de Aysén",
    ap: "Región de Arica y Parinacota",
    "arica y parinacota": "Región de Arica y Parinacota",
    magallanes: "Región de Magallanes",
    "region aysen del general carlos ibanez del campo": "Región de Aysén",
    "aysen del general carlos ibanez del campo": "Región de Aysén",
    "region de aysen del general carlos ibanez del campo": "Región de Aysén",
    "region de magallanes": "Región de Magallanes",
    "region de magallanes y antartica chilena": "Región de Magallanes",
    "magallanes y antartica chilena": "Región de Magallanes",
    "magallanes y la antartica chilena": "Región de Magallanes",
    "region de magallanes y la antartica chilena": "Región de Magallanes",
    "bio bio": "Región del Biobío",
    "region del bio bio": "Región del Biobío",
    "region del biobio": "Región del Biobío",
    "la araucania": "Región de la Araucanía",
    "region de la araucania": "Región de la Araucanía",
    "libertador general bernardo o'higgins": "Región del Libertador General Bernardo O'Higgins",
    "region del libertador general bernardo o'higgins": "Región del Libertador General Bernardo O'Higgins",
    li: "Región del Libertador General Bernardo O'Higgins",
    vs: "Región de Valparaíso",
    ml: "Región del Maule"
};

function normalizeRegionKey(value) {
    if (!value) {
        return "";
    }
    return String(value)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s']/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function mapRegionLabel(rawRegion) {
    if (!rawRegion) {
        return "";
    }
    const key = normalizeRegionKey(rawRegion);
    return REGION_ALIAS_TO_LABEL[key] || rawRegion;
}

function normalizeComunaKey(value) {
    return normalizeRegionKey(value);
}

function getComponent(place, type, attribute) {
    if (!place || !place.address_components) {
        return "";
    }
    const component = place.address_components.find((c) => c.types && c.types.includes(type));
    return component ? (component[attribute] || "") : "";
}

function resultMatchesComuna(result, expectedComuna) {
    if (!expectedComuna || !result || !result.address_components) {
        return !expectedComuna;
    }
    const expected = normalizeComunaKey(expectedComuna);
    const types = [
        "administrative_area_level_3",
        "administrative_area_level_2",
        "locality",
        "sublocality",
        "sublocality_level_1"
    ];
    return result.address_components.some((component) => {
        if (!component.types || !component.types.some((t) => types.indexOf(t) !== -1)) {
            return false;
        }
        const longName = normalizeComunaKey(component.long_name);
        const shortName = normalizeComunaKey(component.short_name);
        return longName === expected
            || shortName === expected
            || longName.indexOf(expected) !== -1
            || expected.indexOf(longName) !== -1;
    });
}

function resultMatchesRegion(result, expectedRegion) {
    if (!expectedRegion || !result || !result.address_components) {
        return false;
    }
    const expected = normalizeRegionKey(mapRegionLabel(expectedRegion));
    return result.address_components.some((component) => {
        if (!component.types || component.types.indexOf("administrative_area_level_1") === -1) {
            return false;
        }
        return normalizeRegionKey(mapRegionLabel(component.long_name)) === expected
            || normalizeRegionKey(mapRegionLabel(component.short_name)) === expected;
    });
}

/**
 * Region the catalog (stateAndCityJson, rendered as data-state-and-city-list) assigns to a
 * comuna. Empty when the comuna is unknown: the region fallback must never trust a residual
 * #region (the Santiago bias of ticket 258178).
 */
function findRegionByComunaInCatalog(stateAndCityList, comuna) {
    if (!stateAndCityList || !comuna) {
        return "";
    }
    const wanted = normalizeComunaKey(comuna);
    const region = Object.keys(stateAndCityList).find((regionName) =>
        (stateAndCityList[regionName] || []).some((city) => normalizeComunaKey(city) === wanted));
    return region || "";
}

function getExpectedRegionForComuna(comuna) {
    const $catalog = $(".js-states-and-cities-json");
    const stateAndCityList = $catalog.length ? $catalog.data("state-and-city-list") : null;
    return findRegionByComunaInCatalog(stateAndCityList, comuna);
}

function pickGeocodeResult(results, expectedComuna, expectedRegion) {
    if (!results || !results.length) {
        return null;
    }
    if (!expectedComuna) {
        return results[0];
    }
    const byComuna = results.find((r) => resultMatchesComuna(r, expectedComuna));
    if (byComuna) {
        return byComuna;
    }
    // Comuna não casou (Google pode nomeá-la diferente): aceitar um resultado da mesma
    // região, nunca um de outra (o ponto fixo de Santiago do ticket 258178).
    return results.find((r) => resultMatchesRegion(r, expectedRegion)) || null;
}

/**
 * AutoComplete class for Google Places API
 * @class
 * @param {NodeList} targetInputs - list of input elements to apply autocomplete to
 * @param {function} callbackOnPlaceChanged - callback function to execute when place is changed
 */
function AutoComplete(targetInputs, callbackOnPlaceChanged) {
    const PLACE_FIELDS_CONFIGURATIONS = {
        address1: [
            { type: "street_number", attribute: "short_name" },
            { type: "route", attribute: "long_name", separator: " " }
        ],
        address2: [
            { type: "route", attribute: "long_name", separator: " " }
        ],
        postBox: [
            { type: "street_number", attribute: "short_name" }
        ],
        // Prefer long_name so we get "Región de Valparaíso" instead of "VA"/"RM"
        state: [
            { type: "administrative_area_level_1", attribute: "long_name" }
        ],
        city: [
            { type: "administrative_area_level_3", attribute: "long_name" }
        ],
        country: [
            { type: "country", attribute: "long_name" }
        ],
        countryCode: [
            { type: "country", attribute: "short_name" }
        ],
    };

    const $configurations = $("[data-autocomplete-configurations]");
    const fields = $configurations.data("fields");

    this.targets = targetInputs || document.querySelectorAll("[data-autocomplete-target]") || [];
    this.countryCode = $configurations.length ? $configurations.data("country-code").split(",") : "";
    this.apiKey = $configurations.length ? $configurations.data("place-api-key") : {};
    this.fields = fields ? fields.split(",") : ["address_component", "geometry"];
    this.callbackOnPlaceChanged = callbackOnPlaceChanged;
    this.addressModal = $("#addAddressModal .modal-body");

    const self = this;

    this.init = function () {
        if (window.google && window.google.maps) {
            this.initAutocomplete();
            return;
        }

        if (!this.apiKey) {
            return;
        }

        window.initAutocomplete = this.initAutocomplete.bind(this);
    };

    this.initAutocomplete = async function () {
        if (!this.targets) {
            return;
        }

        const [Places] = await Promise.all([google.maps.importLibrary("places")]);

        this.targets.forEach((element) => {
            const $element = $(element);

            if ($element.data("isInitialized")) {
                return;
            } else {
                $element.data("isInitialized", true);
                const scrollTarget = self.addressModal.length ? self.addressModal : $(window);
                scrollTarget.on("scroll", self.positionPacContainer);
            }

            element.addEventListener("input", async () => {

                var now = new Date().getTime();
                var diferenca = now - (window.googlePlaceUltimaExecucao ? window.googlePlaceUltimaExecucao : 0);
                window.googlePlaceCaracteres = window.googlePlaceCaracteres ? window.googlePlaceCaracteres + 1 : 1;

                if (($element.val().match(/[0-9]{1}/) != null) || (window.googlePlaceCaracteres >= (window.googlePlaceCantidad ? window.googlePlaceCantidad : 3)) || (window.googlePlaceUltimaExecucao && diferenca >= (window.googlePlaceTiempo ? window.googlePlaceTiempo : 1000))) {
                    window.googlePlaceUltimaExecucao = now;
                    window.googlePlaceCaracteres  = 0;
                    if (!$element.data("placesAutoComplete")) {
                        const autocomplete = new Places.Autocomplete(element, {
                            componentRestrictions: { country: self.countryCode },
                            fields: self.fields
                        });

                        autocomplete.element = element;
                        autocomplete.addListener("place_changed", self.onPlaceChanged);

                        $element.data("placesAutoComplete", autocomplete);
                        self.watchPacContainer();
                    }
                } else {
                    if ($element.data("placesAutoComplete")) {
                        google.maps.event.clearInstanceListeners(element);
                        $element.data("placesAutoComplete", null);
                        $(".pac-container").remove();
                    }
                }
            });
        });
    };


    this.onPlaceChanged = function () {
        const place = this.getPlace();
        const address = self.getAddress(place);
        const $container = $(this.element).closest("form");

        if (!address) {
            return;
        }

        // Clear residual region/city/coords from a previous address before applying the new place.
        ["state", "city", "latitud", "longitud"].forEach((field) => {
            const $field = $container.find(`[data-autocomplete="${field}"]`);
            if ($field.length) {
                $field.val("");
            }
        });

        Object.keys(address).forEach((field) => {
            const $field = $container.find(`[data-autocomplete="${field}"]`);

            if ($field.length) {
                $field.val(address[field]);
            }
        });

        if (self.callbackOnPlaceChanged) {
            self.callbackOnPlaceChanged($container);
        }
    };

    this.getAddress = function (place) {
        if (!place || !place.address_components) {
            return;
        }

        const address = Object.keys(PLACE_FIELDS_CONFIGURATIONS).reduce((acc, field) => {
            const fieldConfig = PLACE_FIELDS_CONFIGURATIONS[field];

            fieldConfig.forEach((config) => {
                const component = place.address_components.find((c) => c.types.includes(config.type));

                acc[field] = component
                    ? [acc[field], component[config.attribute]].filter(Boolean).join(config.separator || " ")
                    : "";
            });

            return acc;
        }, {});

        // Fallback comuna quando falta administrative_area_level_3. Em Chile, o nível 2 é a
        // PROVÍNCIA ("Marga Marga", "Cordillera"), não a comuna: só aceitar um candidato que
        // exista no catálogo de comunas quando o catálogo está na página; locality vem primeiro.
        if (!address.city) {
            const $catalog = $(".js-states-and-cities-json");
            const stateAndCityList = $catalog.length ? $catalog.data("state-and-city-list") : null;
            const candidates = [
                getComponent(place, "locality", "long_name"),
                getComponent(place, "administrative_area_level_2", "long_name")
            ].filter(Boolean);
            address.city = candidates.find((candidate) => !stateAndCityList
                || findRegionByComunaInCatalog(stateAndCityList, candidate)) || "";
        }

        address.state = mapRegionLabel(address.state);

        if (place.geometry && place.geometry.location) {
            address.latitud = place.geometry.location.lat();
            address.longitud = place.geometry.location.lng();
        }

        return address;
    };

    this.watchPacContainer = function () {
        const targetNode = document.querySelector("body");
        const config = { childList: true, subtree: true };

        const callback = (mutationList, observer) => {
            mutationList.forEach((mutation) => {
                if (mutation.target?.classList.contains("pac-container") && mutation.addedNodes?.length) {
                    self.positionPacContainer();
                    observer.disconnect();
                }
            });
        };

        const observer = new MutationObserver(callback);
        observer.observe(targetNode, config);
    };

    this.positionPacContainer = function () {
        const target = self.targets[0];

        if (document.activeElement === target) {
            const { width, height } = target.getBoundingClientRect();
            const { left, top } = $(target).offset();
            const borderLeftWidth = parseFloat($(target).css("border-left-width").replace("px", ""));
            const borderRightWidth = parseFloat($(target).css("border-right-width").replace("px", ""));

            $(".pac-container").css({
                width: `${width - (borderLeftWidth + borderRightWidth)}px`,
                top: `${height + top}px`,
                left: `${left}px`,
                display: "block"
            });
        }
    };

}

/**
 * Geocode manual address without trusting a residual/wrong region label.
 * Rejects results whose comuna does not match #city (avoids Santiago fallback).
 */
async function validateAddressOnGoogleGeocoder() {
    const { Geocoder } = await google.maps.importLibrary("geocoding");
    let isAddressValid = false;

    const street = ($("#address2").val() || "").trim();
    const number = ($("#postBox").val() || "").trim();
    const city = ($("#city").val() || "").trim();
    const region = getExpectedRegionForComuna(city);
    const country = ($("#country").val() || "Chile").trim();
    // Build query WITHOUT region so a stale "Región Metropolitana" cannot bias Google to Santiago.
    const addressQuery = [number, street, city, country].filter(Boolean).join(", ");

    let $latitud = $("#latitud");
    let $longitud = $("#longitud");

    const geocoder = new Geocoder();
    await new Promise((resolve) => {
        geocoder.geocode({ address: addressQuery, componentRestrictions: { country: "CL" } }, function (result, status) {
            const matched = status === "OK" ? pickGeocodeResult(result, city, region) : null;
            if (matched && matched.geometry && matched.geometry.location) {
                $latitud.val(matched.geometry.location.lat());
                $longitud.val(matched.geometry.location.lng());
                // Keep #region in sync with Google long_name when available
                const regionFromResult = getComponent(matched, "administrative_area_level_1", "long_name");
                if (regionFromResult) {
                    $("#region").val(mapRegionLabel(regionFromResult));
                }
                isAddressValid = true;
            } else {
                $latitud.val("");
                $longitud.val("");
                isAddressValid = false;
            }
            resolve();
        });
    });

    $("body").trigger("address:submit:address", isAddressValid);
    return isAddressValid;
}

/**
 * Initializes an instance of the AutoComplete class and calls its init method
 * @param {Array} targetInputs - an array of input elements to attach the autocomplete functionality to
 * @param {Function} callbackOnPlaceChanged - a callback function to execute when the user selects a place from the autocomplete dropdown
 * @returns {void}
 */
function initAutocomplete(targetInputs, callbackOnPlaceChanged) {
    const autocomplete = new AutoComplete(targetInputs, callbackOnPlaceChanged);
    autocomplete.init();
}

module.exports = {
    initAutocomplete: initAutocomplete,
    validateAddressOnGoogleGeocoder,
    mapRegionLabel,
    pickGeocodeResult,
    resultMatchesRegion,
    findRegionByComunaInCatalog
};
